import { create } from 'zustand';
import type { ReactNode } from 'react';

export interface LoadingMessage {
    id?: number | string;
    title?: string;
    message?: ReactNode;
    inBackground?: boolean;
}

interface LoadingMessageStore {
    loadingMessages: LoadingMessage[];
    addLoadingMessage: (message: LoadingMessage) => void;
    updateLoadingMessage: (id: number | string, message: Partial<LoadingMessage>) => void;
    removeLoadingMessage: (message: LoadingMessage) => void;
    clearLoadingMessages: () => void;
}

export const useLoadingMessageStore = create<LoadingMessageStore>((set) => ({
    loadingMessages: [],

    addLoadingMessage: (message) =>
        set((state) => ({ loadingMessages: [...state.loadingMessages, message] })),

    updateLoadingMessage: (id, message) =>
        set((state) => ({
            loadingMessages: state.loadingMessages.map((item) =>
                item.id === id ? { ...item, ...message, id } : item,
            ),
        })),

    removeLoadingMessage: (message) =>
        set((state) => ({
            loadingMessages: state.loadingMessages.filter(
                (m) =>
                    (message.id && m.id !== message.id) ||
                    (!message.id && !(m.title === message.title && m.message === message.message))
            ),
        })),

    clearLoadingMessages: () => set({ loadingMessages: [] }),
}));

export async function withLoadingMessage<T>(
    func: () => Promise<T> | T,
    message: LoadingMessage
): Promise<T> {
    const { addLoadingMessage, removeLoadingMessage } = useLoadingMessageStore.getState();
    try {
        addLoadingMessage(message);
        return await Promise.resolve(func());
    } finally {
        removeLoadingMessage(message);
    }
}

export class LoadingContext {
    private id: number;
    private defaultTitle: string;
    private inBackground: boolean;

    constructor() {
        this.id = Math.random();
        this.defaultTitle = 'Loading...';
        this.inBackground = false;
    }

    setDefaultTitle(title: string) {
        this.defaultTitle = title;
    }

    setToBackground(status: boolean) {
        this.inBackground = status;
    }

    setMessage(message: Partial<LoadingMessage>) {
        const { loadingMessages, addLoadingMessage, updateLoadingMessage } =
            useLoadingMessageStore.getState();
        const nextMessage: LoadingMessage = {
            title: this.defaultTitle,
            inBackground: this.inBackground,
            ...message,
            id: this.id,
        };
        if (loadingMessages.some((item) => item.id === this.id)) {
            updateLoadingMessage(this.id, nextMessage);
            return;
        }
        addLoadingMessage(nextMessage);
    }

    clearAll() {
        useLoadingMessageStore.getState().clearLoadingMessages();
    }

    dispose() {
        useLoadingMessageStore.getState().removeLoadingMessage({ id: this.id });
    }
}
